import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BusinessUnitProvider } from './context/BusinessUnitContext';
import { GlobalSettingsProvider } from './context/GlobalSettingsContext';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Layout from './components/Layout';
import './index.css';

// Route-level code splitting: each page downloads only when first visited
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Contacts = lazy(() => import('./pages/Contacts'));
const Leads = lazy(() => import('./pages/Leads'));
const Pipeline = lazy(() => import('./pages/Pipeline'));
const DisqualifiedLeads = lazy(() => import('./pages/DisqualifiedLeads'));
const ArchivedDeals = lazy(() => import('./pages/ArchivedDeals'));
const Activities = lazy(() => import('./pages/Activities'));
const BusinessUnits = lazy(() => import('./pages/BusinessUnits'));
const Import = lazy(() => import('./pages/Import'));
const Reports = lazy(() => import('./pages/Reports'));
const LeadGeneratorReport = lazy(() => import('./pages/LeadGeneratorReport'));
const LeadFinder = lazy(() => import('./pages/LeadFinder'));
const UserPerformance = lazy(() => import('./pages/UserPerformance'));
const UserPerformanceDetail = lazy(() => import('./pages/UserPerformanceDetail'));
const Settings = lazy(() => import('./pages/Settings'));
const DeletedItems = lazy(() => import('./pages/DeletedItems'));
const NextSteps = lazy(() => import('./pages/NextSteps'));

const PageFallback = () => (
  <div className="flex items-center justify-center py-24" data-testid="page-loading">
    <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // CRITICAL: If returning from OAuth callback with session_id in hash,
  // let AuthCallback handle it first
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? (
    <GlobalSettingsProvider>
      <BusinessUnitProvider>{children}</BusinessUnitProvider>
    </GlobalSettingsProvider>
  ) : <Navigate to="/login" replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Layout />
              </PrivateRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="leads" element={<Leads />} />
            <Route path="pipeline" element={<Pipeline />} />
            <Route path="disqualified-leads" element={<DisqualifiedLeads />} />
            <Route path="archived" element={<ArchivedDeals />} />
            <Route path="activities" element={<Activities />} />
            <Route path="business-units" element={<BusinessUnits />} />
            <Route path="import" element={<Import />} />
            <Route path="reports" element={<Reports />} />
            <Route path="lead-generator-report" element={<LeadGeneratorReport />} />
            <Route path="lead-finder" element={<LeadFinder />} />
            <Route path="user-performance" element={<UserPerformance />} />
            <Route path="user-performance/:userId" element={<UserPerformanceDetail />} />
            <Route path="deleted-items" element={<DeletedItems />} />
            <Route path="next-steps" element={<NextSteps />} />
            <Route path="settings" element={<Settings />} />
          </Route>
        </Routes>
        </Suspense>
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
